import type {TimelineEvent, TimelineSummary, CommitTimelineEvent} from '../routes/route-payload-types'

export type EventGroup = {
  label: string
  type: string
  count: number
  events: TimelineEvent[]
  link?: string
}

/** Build list of event groups to display */
export function buildEventGroups(summary: TimelineSummary, changesUrl: string): EventGroup[] {
  const items: EventGroup[] = []

  // Combine all reviewer events (approvals, changes requested, reviews) into a single group
  // Tag each event with its type so we can render them correctly
  const allReviewerEvents = [
    ...summary.approvals.events.map(e => ({...e, eventType: 'approval' as const})),
    ...summary.changesRequested.events.map(e => ({...e, eventType: 'changes-requested' as const})),
    ...summary.comments.events.map(e => ({...e, eventType: 'review' as const})),
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())

  if (allReviewerEvents.length > 0) {
    const label = buildReviewerActivityLabel(
      summary.approvals.events,
      summary.changesRequested.events,
      summary.comments.events,
    )

    items.push({
      label,
      type: 'reviewer-activity',
      count: allReviewerEvents.length,
      events: allReviewerEvents,
    })
  }

  if (summary.issueComments.events.length > 0) {
    items.push({
      label: `${summary.issueComments.events.length} ${summary.issueComments.events.length === 1 ? 'comment' : 'comments'}${formatUserList(summary.issueComments.events)}`,
      type: 'comment',
      count: summary.issueComments.events.length,
      events: summary.issueComments.events,
    })
  }

  if (summary.commits.events.length > 0) {
    let commitRangeLink: string | undefined

    // Build link to Files tab scoped to commit range
    // Use baseSha from first commit and sha from last commit
    const firstCommit = summary.commits.events[0] as CommitTimelineEvent
    const lastCommit = summary.commits.events[summary.commits.events.length - 1] as CommitTimelineEvent

    if (firstCommit?.additionalInfo.baseSha && lastCommit?.additionalInfo.sha) {
      commitRangeLink = `${changesUrl}/${firstCommit.additionalInfo.baseSha}..${lastCommit.additionalInfo.sha}`
    }

    items.push({
      label: `${summary.commits.events.length} ${summary.commits.events.length === 1 ? 'commit' : 'commits'}${formatUserList(summary.commits.events)}`,
      type: 'commit',
      count: summary.commits.events.length,
      events: summary.commits.events,
      link: commitRangeLink,
    })
  }

  if (summary.closed.events.length > 0) {
    items.push({
      label: `Closed${formatUserList(summary.closed.events)}`,
      type: 'closed',
      count: summary.closed.events.length,
      events: summary.closed.events,
    })
  }

  if (summary.reopened.events.length > 0) {
    items.push({
      label: `Reopened${formatUserList(summary.reopened.events)}`,
      type: 'reopened',
      count: summary.reopened.events.length,
      events: summary.reopened.events,
    })
  }

  if (summary.markedAsDraft.events.length > 0) {
    items.push({
      label: `Marked as draft${formatUserList(summary.markedAsDraft.events)}`,
      type: 'marked-as-draft',
      count: summary.markedAsDraft.events.length,
      events: summary.markedAsDraft.events,
    })
  }

  if (summary.readyForReview.events.length > 0) {
    items.push({
      label: `Ready for review${formatUserList(summary.readyForReview.events)}`,
      type: 'ready-for-review',
      count: summary.readyForReview.events.length,
      events: summary.readyForReview.events,
    })
  }

  if (summary.labeled.events.length > 0) {
    items.push({
      label: `${summary.labeled.events.length} label ${summary.labeled.events.length === 1 ? 'change' : 'changes'}${formatUserList(summary.labeled.events)}`,
      type: 'labeled',
      count: summary.labeled.events.length,
      events: summary.labeled.events,
    })
  }

  if (summary.reviewRequested.events.length > 0) {
    items.push({
      label: `${summary.reviewRequested.events.length} review ${summary.reviewRequested.events.length === 1 ? 'request change' : 'request changes'}${formatUserList(summary.reviewRequested.events)}`,
      type: 'review-requested',
      count: summary.reviewRequested.events.length,
      events: summary.reviewRequested.events,
    })
  }

  if (summary.reviewDismissed.events.length > 0) {
    items.push({
      label: `${summary.reviewDismissed.events.length} review${summary.reviewDismissed.events.length === 1 ? '' : 's'} dismissed${formatUserList(summary.reviewDismissed.events)}`,
      type: 'review-dismissed',
      count: summary.reviewDismissed.events.length,
      events: summary.reviewDismissed.events,
    })
  }

  if (summary.forcePushed.events.length > 0) {
    items.push({
      label: `Force pushed ${summary.forcePushed.events.length} ${summary.forcePushed.events.length === 1 ? 'time' : 'times'}${formatUserList(summary.forcePushed.events)}`,
      type: 'force-pushed',
      count: summary.forcePushed.events.length,
      events: summary.forcePushed.events,
    })
  }

  if (summary.merged.events.length > 0) {
    items.push({
      label: `Merged${formatUserList(summary.merged.events)}`,
      type: 'merged',
      count: summary.merged.events.length,
      events: summary.merged.events,
    })
  }

  if (summary.baseChanged.events.length > 0) {
    items.push({
      label: `Base branch changed${formatUserList(summary.baseChanged.events)}`,
      type: 'base-changed',
      count: summary.baseChanged.events.length,
      events: summary.baseChanged.events,
    })
  }

  if (summary.baseChangeFailed.events.length > 0) {
    items.push({
      label: `Automatic base change failed${formatUserList(summary.baseChangeFailed.events)}`,
      type: 'base-change-failed',
      count: summary.baseChangeFailed.events.length,
      events: summary.baseChangeFailed.events,
    })
  }

  return items
}

/** Get the verb for the event type */
const EVENT_VERB_MAP: Record<string, string> = {
  approval: 'approved',
  'changes-requested': 'requested changes',
  review: 'reviewed',
  comment: 'commented',
  commit: 'committed',
  closed: 'closed',
  reopened: 'reopened',
  'marked-as-draft': 'marked as draft',
  'ready-for-review': 'marked ready for review',
  labeled: 'labeled',
  'review-requested': 'requested review',
  'review-dismissed': 'dismissed review',
  'force-pushed': 'force pushed',
  merged: 'merged',
  'base-changed': 'changed base branch',
  'base-change-failed': 'automatic base change failed',
}

export function getEventVerb(eventType: string): string {
  return EVENT_VERB_MAP[eventType] ?? ''
}

/** Format a truncated list of teams, showing first N teams and "X others" if more */
export function formatTruncatedTeams(teams: string[], maxTeams: number = 2): string {
  if (teams.length === 0) {
    return ''
  }

  const displayTeams = teams.slice(0, maxTeams)
  const remainingCount = teams.length - displayTeams.length

  let teamList: string
  if (displayTeams.length === 2 && remainingCount === 0) {
    teamList = displayTeams.map(t => `@${t}`).join(' and ')
  } else {
    teamList = displayTeams.map(t => `@${t}`).join(', ')
    if (remainingCount > 0) {
      teamList += `, and ${remainingCount} ${remainingCount === 1 ? 'other' : 'others'}`
    }
  }

  return teamList
}

/** Format a user list from events, showing first 2 users and "X others" if more */
function formatUserList(events: TimelineEvent[], maxUsers: number = 2): string {
  // Extract unique users by login
  const uniqueUsers = Array.from(new Map(events.map(event => [event.user.login, event.user])).values())

  if (uniqueUsers.length === 0) {
    return ''
  }

  // Show first N users
  const displayUsers = uniqueUsers.slice(0, maxUsers)
  const remainingCount = uniqueUsers.length - displayUsers.length

  let userList: string
  if (displayUsers.length === 2 && remainingCount === 0) {
    userList = displayUsers.map(u => u.login).join(' and ')
  } else {
    userList = displayUsers.map(u => u.login).join(', ')
    if (remainingCount > 0) {
      userList += `, and ${remainingCount} ${remainingCount === 1 ? 'other' : 'others'}`
    }
  }

  return ` by ${userList}`
}

/** Build a summary label showing total review count and specific approval/changes requested counts */
function buildReviewerActivityLabel(
  approvals: TimelineEvent[],
  changesRequested: TimelineEvent[],
  reviews: TimelineEvent[],
): string {
  const total = approvals.length + changesRequested.length + reviews.length

  // Main count
  const mainCount = `${total} ${total === 1 ? 'review' : 'reviews'} completed`

  // Specific counts for approvals and changes requested (not comments)
  const specificCounts: string[] = []

  if (approvals.length > 0) {
    specificCounts.push(`${approvals.length} approved`)
  }

  if (changesRequested.length > 0) {
    specificCounts.push(`${changesRequested.length} requested changes`)
  }

  if (specificCounts.length === 0) {
    return mainCount
  }

  return `${mainCount}, ${specificCounts.join(' and ')}`
}
